import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';

interface AddNoteParams {
  clientId: string;
  title: string;
  description: string;
}

interface DeleteNoteParams {
  clientId: string;
  noteId: string;
}

interface PinNoteParams {
  clientId: string;
  noteId: string;
  pinned: boolean;
}

const addNote = async (params: AddNoteParams) => {
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_ADD_CLIENT_NOTE}`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to add note');
  }

  return response.json();
};

const deleteNote = async (params: DeleteNoteParams) => {
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_DELETE_CLIENT_NOTE}`;
  const response = await fetch(apiUrl, {
    method: 'POST', // The backend allows POST for delete logic
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to delete note');
  }

  return response.json();
};

const pinNote = async (params: PinNoteParams) => {
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_PIN_CLIENT_NOTE}`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to pin note');
  }

  return response.json();
};

export const useAddClientNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addNote,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clientNotes', variables.clientId] });
      queryClient.invalidateQueries({ queryKey: ['clientOverview', variables.clientId] });
    },
  });
};

export const useDeleteClientNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNote,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clientNotes', variables.clientId] });
      queryClient.invalidateQueries({ queryKey: ['clientOverview', variables.clientId] });
    },
  });
};

export const usePinClientNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pinNote,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clientNotes', variables.clientId] });
      queryClient.invalidateQueries({ queryKey: ['clientOverview', variables.clientId] });
    },
  });
};
